import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import { OrderStopType } from "@/data/order";
import type { RouteStopDto, TravelOrderDto, TripDto } from "@/api/trips/dto/trip.dto";
import { RouteStopState } from "@/enums/trip";
import { RouteStopStatus, TravelOrderCancellationStatusMap, TripDateField } from "@/data/trip";
import type { TripMonitorItem } from "@/api/trips/dto/trip-monitor";
import dayjs, { type Dayjs } from "dayjs";

export function useTrip() {
  const api = new API();

  function numerateAllRouteStops(routeStops: RouteStopDto[]): RouteStopDto[] {
    let pickupNum = 1;
    let deliveryNum = 1;
    let reloadPickupNum = 1;
    let reloadDeliveryNum = 1;

    return routeStops.map((stop) => {
      if (stop.isReload) {
        if (stop.type === OrderStopType.pickup) {
          return { ...stop, number: reloadPickupNum++ };
        }
        if (stop.type === OrderStopType.delivery) {
          return { ...stop, number: reloadDeliveryNum++ };
        }
      } else {
        if (stop.type === OrderStopType.pickup) {
          return { ...stop, number: pickupNum++ };
        }
        if (stop.type === OrderStopType.delivery) {
          return { ...stop, number: deliveryNum++ };
        }
      }

      return stop;
    });
  }

  async function createTripOverview(tripId: number): Promise<TripDto | undefined> {
    const { showSnackbarError } = useSnackbarStore();

    try {
      const response = await api.trips.getTripById(tripId);
      const tripOverview = response.data;

      if (!tripOverview) return;

      const updatedRoutes = await Promise.all(
        tripOverview.routes.map(async (route) => {
          if (!route.truckId) {
            return route;
          }

          const truckResponse = await api.trucks.findTruckById(route.truckId);

          const updatedTravelOrders = await Promise.all(
            route.travelOrders.map(async (travelOrder) => {
              const orderResponse = await api.orders.findOrderById(travelOrder.orderId);
              if (!orderResponse.success || !orderResponse.data) {
                return travelOrder;
              }

              return { ...travelOrder, order: orderResponse.data };
            }),
          );

          const updatedStops = numerateAllRouteStops(route.routeStops);

          return {
            ...route,
            truck: truckResponse?.data,
            travelOrders: updatedTravelOrders,
            routeStops: updatedStops,
          };
        }),
      );

      return {
        ...tripOverview,
        routes: updatedRoutes,
      };
    } catch (error) {
      console.error(error);
      showSnackbarError(`Could not create trip overview ${error}`);
    }
  }

  function getLoadedMiles(routeDistance: Record<string, number>) {
    return Object.values(routeDistance).reduce((sum, distance) => distance + sum, 0);
  }

  function createEmptyRouteStop(): RouteStopDto {
    return {
      position: 1,
      type: OrderStopType.pickup,
      isReload: false,
      facilityId: 0,
      state: RouteStopState.normal,
      orderStopId: null,
      freights: [],
      timezone: "",
      timeType: "",
      timeTo: "",
      status: RouteStopStatus.notStarted,
      checkInTime: null,
      loadUnloadTime: null,
      verifyTime: null,
      verifyDispatcherId: null,
      podSignedBy: null,
      checkOutTime: null,
      bolFiles: [],
      podFiles: [],
      otherFiles: [],
      order: null,
      oldFacilityId: null,
    };
  }

  function isTravelOrderCancelled(travelOrder: TravelOrderDto): boolean {
    return TravelOrderCancellationStatusMap.some((s) => s.id === travelOrder.status);
  }

  function getExtremeDate(values: (string | null)[], type: "min" | "max"): Dayjs | null {
    const validDates = values.filter((v) => v);

    if (!validDates.length) {
      return null;
    }

    const dayjsValues = validDates.map((v) => dayjs(v));

    const minValue = dayjs.min(dayjsValues);
    const maxValue = dayjs.max(dayjsValues);

    if (!minValue || !maxValue) {
      return null;
    }

    return type === "min" ? minValue : maxValue;
  }

  function completeFormTrips(trips: TripMonitorItem[]): TripMonitorItem[] {
    return trips.map((trip) => {
      const nextStopTimes = trip.routes.map((r) => r.nextRouteStop?.timeTo || null);
      const etaTimes = trip.routes.map((r) => r.lastCheckCall?.eta || null);
      const checkCallTimes = trip.routes.map((r) => r.lastCheckCall?.checkCallTime || null);
      const checkCallTimerTimers = trip.routes.map((r) => r.nextCheckCallTime);
      const updateTimerTimes = trip.routes.flatMap((r) => r.travelOrders.map((to) => to.nextUpdateTime));

      return {
        ...trip,
        nextStopSoonestDate: getExtremeDate(nextStopTimes, "min")?.toISOString() ?? null,
        nextStopLatestDate: getExtremeDate(nextStopTimes, "max")?.toISOString() ?? null,
        etaSoonestDate: getExtremeDate(etaTimes, "min")?.toISOString() ?? null,
        etaLatestDate: getExtremeDate(etaTimes, "max")?.toISOString() ?? null,
        lastCheckCallSoonestDate: getExtremeDate(checkCallTimes, "min")?.toISOString() ?? null,
        lastCheckCallLatestDate: getExtremeDate(checkCallTimes, "max")?.toISOString() ?? null,
        checkCallTimersSoonestDate: getExtremeDate(checkCallTimerTimers, "min")?.toISOString() ?? null,
        checkCallTimerLatestDate: getExtremeDate(checkCallTimerTimers, "max")?.toISOString() ?? null,
        updateTimerSoonestDate: getExtremeDate(updateTimerTimes, "min")?.toISOString() ?? null,
        updateTimerLatestDate: getExtremeDate(updateTimerTimes, "max")?.toISOString() ?? null,
      };
    });
  }

  function sortTripsByDate(
    trips: TripMonitorItem[],
    field: TripDateField,
    mode: "soonest" | "latest" = "soonest",
  ): TripMonitorItem[] {
    return [...trips].sort((a, b) => {
      const aDateStr = a[field];
      const bDateStr = b[field];

      if (!aDateStr && !bDateStr) {
        return 0;
      }

      if (!aDateStr) {
        return 1;
      }

      if (!bDateStr) {
        return -1;
      }

      const aTime = dayjs(aDateStr);
      const bTime = dayjs(bDateStr);

      return mode === "latest" ? bTime.diff(aTime) : aTime.diff(bTime);
    });
  }

  return {
    createTripOverview,
    getLoadedMiles,
    createEmptyRouteStop,
    isTravelOrderCancelled,
    completeFormTrips,
    sortTripsByDate,
  };
}
